import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { RANGE_OUT_ALERT_MESSAGE, RECOMMENDATION, SELECT_EXTERNAL_LINK_NAME } from 'shared-modules/constants/select';
import { formatCurrencyLabel } from 'shared-modules/utils/select';
import { FX, SERVICE_SHORT_NAME_DEF } from 'shared-modules/constants';
import classNames from 'classnames';
import SelectionImage from '../../../../components/SelectionImage';
import { RangeOutAlertIcon } from '../../../../components/Icons';
import styles from './cardHeader.module.scss';
import { Button } from '../../../../components';
import { useOpenLink } from '../../../../hooks';

export const CardHeader = memo(
  ({
    image,
    name,
    serviceId,
    featureNames,
    instrumentIds,
    isRangeOut,
    recommendation,
    isShare,
    isRangeOutMessageNeeded,
    externalLinkUrl,
    tagContainerStyle,
  }) => {
    const instrumentList = useSelector((state) => state.settings.instrumentList);
    const symbols = useMemo(() => {
      if (!instrumentIds || !instrumentList) return [];
      return instrumentIds.map((id) => instrumentList?.[id]?.symbol);
    }, [instrumentIds, instrumentList]);

    const openLink = useOpenLink(externalLinkUrl, SELECT_EXTERNAL_LINK_NAME);
    const tags = useMemo(() => {
      return (
        <div className={classNames(styles.tagContainer, tagContainerStyle)}>
          {serviceId && serviceId !== FX && (
            <div className={styles.tagInnerContainer}>
              <div className={styles.tagText}>{SERVICE_SHORT_NAME_DEF[serviceId]}</div>
            </div>
          )}
          {symbols && (
            <div className={styles.tagInnerContainer}>
              <div className={styles.tagText}>{formatCurrencyLabel(symbols, serviceId)}</div>
            </div>
          )}
          {featureNames?.map((featureName) => (
            <div key={featureName} className={styles.tagInnerContainer}>
              <div className={styles.tagText}>{featureName}</div>
            </div>
          ))}
        </div>
      );
    }, [symbols, serviceId, featureNames, tagContainerStyle]);

    return (
      <>
        <div className={styles.headline}>
          <div className={styles.headlineInner}>
            {image && (
              <SelectionImage
                className={styles.image}
                width="48px"
                serviceId={serviceId}
                image={image}
                isShare={isShare}
                showFrameWhenImageExists={false}
              />
            )}
            <div className={styles.lowerRow}>
              <div className={styles.cardItemUpperRightContainer}>
                <div className={styles.upperRow}>
                  <div className={styles.nameContainer}>
                    <div className={styles.nameWithIcon}>
                      <div className={styles.name} style={recommendation ? { width: '200px' } : {}}>
                        {name}
                      </div>
                      {!isRangeOutMessageNeeded && !!isRangeOut && (
                        <div className={styles.icon}>
                          <RangeOutAlertIcon />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {tags}
                {isRangeOutMessageNeeded && !!isRangeOut && (
                  <div className={styles.rangeOutAlert}>
                    <RangeOutAlertIcon />
                    <div className={styles.alertText}>{RANGE_OUT_ALERT_MESSAGE}</div>
                  </div>
                )}
              </div>
              {recommendation && (
                <div className={styles.recommendedTagContainer}>
                  <p className={styles.recommendedTag}>{RECOMMENDATION}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {externalLinkUrl && (
          <div className={styles.buttonArea}>
            <Button className={styles.linkButton} onClick={openLink}>
              <div className={styles.linkCaption}>{SELECT_EXTERNAL_LINK_NAME}</div>
            </Button>
          </div>
        )}
      </>
    );
  },
);

CardHeader.propTypes = {
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  serviceId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  featureNames: PropTypes.arrayOf(PropTypes.string),
  isRangeOut: PropTypes.bool,
  recommendation: PropTypes.bool,
  instrumentIds: PropTypes.arrayOf(PropTypes.string),
  isShare: PropTypes.bool,
  isRangeOutMessageNeeded: PropTypes.bool,
  externalLinkUrl: PropTypes.string,
  tagContainerStyle: PropTypes.string,
};

CardHeader.defaultProps = {
  image: null,
  featureNames: null,
  isRangeOut: false,
  recommendation: false,
  instrumentIds: null,
  isShare: false,
  isRangeOutMessageNeeded: false,
  externalLinkUrl: null,
  tagContainerStyle: undefined,
};
